import { games, getGameColor } from '../data/games';
import { parseScoreType, secondsToTimeString } from './scoreUtils';

/**
 * Draws the share card on a canvas and returns it as a PNG data URL.
 *
 * Rendered **client-side** (D17) because the browser is the only place that
 * has the fonts, the icons and the data together; the Function has none of
 * them and no image library.
 *
 * Fixed 1200x630 (the size every platform crops from), ignoring
 * devicePixelRatio - the output is an upload, not something on screen, so a
 * retina multiplier would only inflate the bytes.
 *
 * Three things here are not style choices:
 *
 *   - `document.fonts.ready` is awaited before any text is drawn. Canvas does
 *     not wait for webfonts; drawn too early, the PNG silently uses a fallback
 *     and looks nothing like the site.
 *   - `img.decode()` is awaited per icon, for the same reason - an undecoded
 *     image draws as nothing at all, with no error.
 *   - Every drawn asset is same-origin. One cross-origin pixel taints the
 *     canvas and `toDataURL()` throws. (This is about *inputs*; the uploaded
 *     output living on a blob host is fine.)
 */

const WIDTH = 1200;
const HEIGHT = 630;

// A fixed row count rather than measuring: canvas text metrics are fiddly and
// would have to be re-measured on every render. Six rows sits comfortably
// under the header, and anything beyond becomes a "+N more" line.
const MAX_ROWS = 6;

const COLORS = {
  bg: '#1a1a2e',
  block: '#0f1225',
  gold: '#f5d76e',
  cream: '#fef9ef',
  text: '#eef0f8',
  muted: '#8892b0',
};

/** The score as it should read on the card. */
export const formatScoreForCard = (game, score) => {
  if (score === 'X' || score === null || score === undefined || score === '') return 'X';

  const info = parseScoreType(game && game.scoreType);
  if (info && info.type === 'time') {
    return secondsToTimeString(score).replace(/^0(?=\d:)/, '');
  }
  return String(score);
};

/**
 * The games to draw, in the order they should appear.
 *
 * Every game scored today, no selection (D46) - dropping the checkboxes
 * removed a consent problem as much as a UI: a promise of control that the
 * live lander would silently override.
 *
 * Order follows the playlist, since that's the order the player sees all day.
 * `favoriteOrder` holds keys like `game-1` and `custom-4`, and custom links
 * can't carry scores, so this is "scored catalog games in favoriteOrder
 * position, then any scored game not in that list".
 *
 * @param {Object} todayPlays gameId -> { score, message }
 * @param {string[]} favoriteOrder
 * @returns {Array<{game: Object, score: *}>}
 */
export const scoredGamesForCard = (todayPlays, favoriteOrder = []) => {
  const scored = Object.entries(todayPlays || {})
    .filter(([, play]) => play && play.score !== null && play.score !== undefined && play.score !== '')
    .map(([gameId, play]) => ({
      game: games.find((g) => String(g.id) === String(gameId)),
      score: play.score,
    }))
    .filter((entry) => !!entry.game);

  const position = (game) => {
    const index = favoriteOrder.indexOf(`game-${game.id}`);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  return scored.sort((a, b) => position(a.game) - position(b.game) || a.game.name.localeCompare(b.game.name));
};

const roundedRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const loadImage = async (src) => {
  const img = new Image();
  img.src = src;
  // Same-origin only - see the note at the top. No crossOrigin attribute,
  // because setting one on a same-origin asset buys nothing and invites
  // someone to point this at a remote URL later.
  await img.decode();
  return img;
};

const drawRow = (ctx, { entry, icon, y }) => {
  const accent = getGameColor(entry.game);

  roundedRect(ctx, 80, y, WIDTH - 160, 62, 14);
  ctx.fillStyle = 'rgba(255,255,255,0.04)';
  ctx.fill();

  if (icon) {
    ctx.save();
    roundedRect(ctx, 92, y + 8, 46, 46, 10);
    ctx.clip();
    ctx.drawImage(icon, 92, y + 8, 46, 46);
    ctx.restore();
  }

  ctx.fillStyle = COLORS.text;
  ctx.font = '600 28px "DM Sans", sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(entry.game.name, 156, y + 32, 700);

  ctx.fillStyle = accent;
  ctx.font = '700 32px "Fraunces", serif';
  ctx.textAlign = 'right';
  ctx.fillText(formatScoreForCard(entry.game, entry.score), WIDTH - 104, y + 32);
};

/**
 * @param {Object} options
 * @param {string} options.username
 * @param {Array<{game: Object, score: *}>} options.entries
 * @param {string} options.dateLabel
 * @returns {Promise<string>} PNG data URL
 */
export const renderShareCard = async ({ username, entries = [], dateLabel }) => {
  // Before any text is drawn, not after - see the note at the top.
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready;
  }

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  roundedRect(ctx, 40, 40, WIDTH - 80, HEIGHT - 80, 28);
  ctx.fillStyle = COLORS.block;
  ctx.fill();

  // Header
  let logo = null;
  try {
    logo = await loadImage('/img/light-logo.png');
  } catch {
    // The card is still legible without it; a missing logo must not cost the
    // whole image.
  }
  if (logo) ctx.drawImage(logo, 76, 64, 62, 72);

  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillStyle = COLORS.cream;
  ctx.font = '700 34px "Fraunces", serif';
  ctx.fillText('NICE LIGHT GAMES', 152, 104);

  ctx.fillStyle = COLORS.muted;
  ctx.font = '500 24px "DM Sans", sans-serif';
  ctx.fillText(dateLabel, 152, 136);

  const shown = entries.slice(0, MAX_ROWS);
  const overflow = entries.length - shown.length;

  if (entries.length === 0) {
    // An invitation, not an apology for an empty card (D46). Someone with no
    // scores yet is exactly who most needs to be able to invite a friend.
    ctx.textAlign = 'center';
    ctx.fillStyle = COLORS.cream;
    ctx.font = '700 58px "Fraunces", serif';
    ctx.fillText(`${username} wants to play with you`, WIDTH / 2, 330);

    ctx.fillStyle = COLORS.muted;
    ctx.font = '500 30px "DM Sans", sans-serif';
    ctx.fillText('A daily round of puzzles — for the company, not the competition.', WIDTH / 2, 390);

    ctx.fillStyle = COLORS.gold;
    ctx.font = '700 32px "DM Sans", sans-serif';
    ctx.fillText('nicelight.games', WIDTH / 2, 520);

    return canvas.toDataURL('image/png');
  }

  ctx.textAlign = 'left';
  ctx.fillStyle = COLORS.gold;
  ctx.font = '700 46px "Fraunces", serif';
  ctx.fillText(`${username}'s round today`, 80, 208);

  const icons = await Promise.all(
    shown.map((entry) => loadImage(`/img/games/${entry.game.image}`).catch(() => null))
  );

  shown.forEach((entry, index) => {
    drawRow(ctx, { entry, icon: icons[index], y: 240 + index * 70 });
  });

  if (overflow > 0) {
    ctx.fillStyle = COLORS.muted;
    ctx.font = '500 26px "DM Sans", sans-serif';
    ctx.fillText(`+${overflow} more`, 92, 240 + shown.length * 70 + 26);
  }

  ctx.textAlign = 'right';
  ctx.fillStyle = COLORS.gold;
  ctx.font = '700 30px "DM Sans", sans-serif';
  ctx.fillText('nicelight.games', WIDTH - 80, HEIGHT - 72);

  return canvas.toDataURL('image/png');
};
